import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Shield, Activity, AlertTriangle, Users, Clock, Monitor,
  Globe, Smartphone, Laptop, Lock, Unlock, Flag, XCircle,
  CheckCircle, Eye, ChevronRight, RefreshCw, Search,
  AlertOctagon, Ban, UserX, FileText, MessageSquare, Star,
  Award, Target, Brain, Mic, Video, BookOpen, ChevronDown,
  ClipboardList, Wifi, WifiOff, Zap, Play
} from "lucide-react";

const UserActivityMonitor = ({ token }) => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSummary, setUserSummary] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [expandedAssessment, setExpandedAssessment] = useState(null);
  const [expandedInterview, setExpandedInterview] = useState(null);
  
  // Real-time WebSocket state
  const [wsConnected, setWsConnected] = useState(false);
  const [liveActivities, setLiveActivities] = useState([]);
  const [activityOverview, setActivityOverview] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // WebSocket connection for real-time updates
  const connectWebSocket = useCallback(() => {
    if (!token) return;
    
    // Construct WebSocket URL
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = API.replace(/^https?:\/\//, '').replace('/api', '');
    const wsUrl = `${wsProtocol}//${wsHost}/api/admin/activity/ws/live?token=${token}`;
    
    console.log('[WS] Connecting to:', wsUrl);
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('[WS] Connected!');
        setWsConnected(true);
        toast.success("Real-time monitoring connected!", { duration: 2000 });
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[WS] Message received:', data);
          
          if (data.type === 'new_activity') {
            // Add to live activities (keep last 20)
            setLiveActivities(prev => [data.activity, ...prev].slice(0, 20));
            
            // Update dashboard counts
            setDashboard(prev => {
              if (!prev) return prev;
              return {
                ...prev,
                today: {
                  ...prev.today,
                  successful_logins: data.activity.activity_type === 'login' 
                    ? prev.today.successful_logins + 1 
                    : prev.today.successful_logins
                }
              };
            });
            
            // Show toast notification
            toast.info(
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-yellow-500" />
                <div>
                  <p className="font-medium">{data.activity.activity_type.replace(/_/g, ' ')}</p>
                  <p className="text-xs text-gray-500">{data.activity.user_email}</p>
                </div>
              </div>,
              { duration: 3000 }
            );
          } else if (data.type === 'heartbeat') {
            // Just keep alive
          }
        } catch (e) {
          console.error('[WS] Parse error:', e);
        }
      };
      
      wsRef.current.onclose = (event) => {
        console.log('[WS] Disconnected:', event.code, event.reason);
        setWsConnected(false);
        
        // Attempt reconnect after 5 seconds
        if (!event.wasClean) {
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error('[WS] Error:', error);
        setWsConnected(false);
      };
    } catch (error) {
      console.error('[WS] Connection error:', error);
    }
  }, [token]);
  
  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchAlerts();
    fetchActivityOverview();
  }, []);

  const fetchActivityOverview = async () => {
    try {
      const response = await axios.get(`${API}/admin/activity/stats/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivityOverview(response.data);
      // Initialize live activities from recent activities
      setLiveActivities(response.data.recent_activities || []);
    } catch (error) {
      console.error("Failed to fetch activity overview:", error);
    }
  };

  const backfillHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/admin/activity/backfill-history`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Backfill completed! ${response.data.activities_created} activities added.`);
      fetchActivityOverview();
      fetchDashboard();
    } catch (error) {
      toast.error("Backfill failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/activity/security/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async (status = null) => {
    try {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      
      const response = await axios.get(`${API}/admin/activity/security/alerts?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error("Failed to fetch alerts:", error);
    }
  };

  const fetchUserSummary = async (userId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/activity/users/${userId}/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserSummary(response.data);
    } catch (error) {
      toast.error("Failed to fetch user details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      setSearching(true);
      const response = await axios.get(`${API}/admin/users?search=${encodeURIComponent(searchQuery)}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data.users || response.data || []);
    } catch (error) {
      toast.error("Search failed");
      console.error(error);
    } finally {
      setSearching(false);
    }
  };

  const handleBlockUser = async (userId) => {
    if (!window.confirm("Are you sure you want to block this user?")) return;
    
    try {
      await axios.post(`${API}/admin/activity/users/${userId}/block`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User blocked successfully");
      if (userSummary && userSummary.user.id === userId) {
        fetchUserSummary(userId);
      }
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to block user");
    }
  };

  const handleUnblockUser = async (userId) => {
    try {
      await axios.post(`${API}/admin/activity/users/${userId}/unblock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User unblocked successfully");
      if (userSummary && userSummary.user.id === userId) {
        fetchUserSummary(userId);
      }
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to unblock user");
    }
  };

  const handleFlagUser = async (userId) => {
    const reason = window.prompt("Enter reason for flagging this user:");
    if (reason === null) return;
    
    try {
      await axios.post(`${API}/admin/activity/users/${userId}/flag?reason=${encodeURIComponent(reason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User flagged for review");
      if (userSummary && userSummary.user.id === userId) {
        fetchUserSummary(userId);
      }
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to flag user");
    }
  };

  const handleUnflagUser = async (userId) => {
    try {
      await axios.post(`${API}/admin/activity/users/${userId}/unflag`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User unflagged");
      if (userSummary && userSummary.user.id === userId) {
        fetchUserSummary(userId);
      }
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to unflag user");
    }
  };

  const handleDismissAlert = async (alertId) => {
    try {
      await axios.put(`${API}/admin/activity/security/alerts/${alertId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Alert dismissed");
      fetchAlerts();
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to dismiss alert");
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await axios.put(`${API}/admin/activity/security/alerts/${alertId}/resolve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Alert resolved");
      fetchAlerts();
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to resolve alert");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString();
  };

  const getAlertLevelBadge = (level) => {
    const styles = {
      critical: "bg-red-100 text-red-700 border-red-300",
      warning: "bg-yellow-100 text-yellow-700 border-yellow-300",
      medium: "bg-orange-100 text-orange-700 border-orange-300",
      info: "bg-blue-100 text-blue-700 border-blue-300"
    };
    return styles[level] || styles.info;
  };

  const getRiskBadge = (score) => {
    if (score >= 75) return "bg-red-100 text-red-700 border-red-300";
    if (score >= 50) return "bg-orange-100 text-orange-700 border-orange-300";
    if (score >= 25) return "bg-yellow-100 text-yellow-700 border-yellow-300";
    return "bg-green-100 text-green-700 border-green-300";
  };

  const getActivityIcon = (type) => {
    const icons = {
      login: <Users className="h-4 w-4" />,
      assessment: <ClipboardList className="h-4 w-4" />,
      interview: <Mic className="h-4 w-4" />,
      skill_dna: <Brain className="h-4 w-4" />,
      career_twin: <Target className="h-4 w-4" />,
      course: <BookOpen className="h-4 w-4" />,
      default: <Activity className="h-4 w-4" />
    };
    return icons[type] || icons.default;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-6 w-6 text-purple-600" />
            User Activity & Security Monitoring
            {wsConnected ? (
              <Badge className="bg-green-100 text-green-700 ml-2 flex items-center gap-1">
                <Wifi className="h-3 w-3" /> Live
              </Badge>
            ) : (
              <Badge className="bg-gray-100 text-gray-600 ml-2 flex items-center gap-1">
                <WifiOff className="h-3 w-3" /> Offline
              </Badge>
            )}
          </h2>
          <p className="text-gray-600 mt-1">Monitor user activities, login patterns, and security alerts</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={connectWebSocket}
            disabled={wsConnected}
            variant="outline" 
            className="gap-2 border-green-300 text-green-700 hover:bg-green-50"
            data-testid="connect-live-btn"
          >
            <Play className="h-4 w-4" />
            {wsConnected ? "Connected" : "Connect Live"}
          </Button>
          <Button 
            onClick={backfillHistory}
            disabled={loading}
            variant="outline" 
            className="gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="backfill-btn"
          >
            <Activity className="h-4 w-4" />
            Backfill History
          </Button>
          <Button 
            onClick={() => { fetchDashboard(); fetchAlerts(); fetchActivityOverview(); }} 
            variant="outline" 
            className="gap-2 border-gray-300 text-gray-700 hover:bg-gray-100"
            data-testid="refresh-monitoring-btn"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-gray-100 border border-gray-300">
          <TabsTrigger value="dashboard" className="gap-2 text-gray-700 data-[state=active]:bg-purple-600 data-[state=active]:text-white" data-testid="monitoring-dashboard-tab">
            <Activity className="h-4 w-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="live-feed" className="gap-2 text-gray-700 data-[state=active]:bg-purple-600 data-[state=active]:text-white" data-testid="monitoring-live-tab">
            <Zap className="h-4 w-4" />
            Live Feed
            {wsConnected && <span className="ml-1 w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>}
          </TabsTrigger>
          <TabsTrigger value="alerts" className="gap-2 text-gray-700 data-[state=active]:bg-purple-600 data-[state=active]:text-white" data-testid="monitoring-alerts-tab">
            <AlertTriangle className="h-4 w-4" />
            Security Alerts
            {dashboard?.alerts?.critical > 0 && (
              <Badge className="bg-red-500 text-white ml-1">{dashboard.alerts.critical}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="users" className="gap-2 text-gray-700 data-[state=active]:bg-purple-600 data-[state=active]:text-white" data-testid="monitoring-users-tab">
            <Users className="h-4 w-4" />
            User Lookup
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="mt-6">
          {dashboard && (
            <div className="space-y-6">
              {/* Activity Overview Stats */}
              {activityOverview && (
                <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-gray-900 flex items-center gap-2 text-lg">
                      <Activity className="h-5 w-5 text-purple-600" />
                      Activity Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white/70 p-3 rounded-lg border border-purple-200">
                        <p className="text-xs text-gray-500">Total Activities</p>
                        <p className="text-2xl font-bold text-purple-700">{activityOverview.total_activities}</p>
                      </div>
                      <div className="bg-white/70 p-3 rounded-lg border border-blue-200">
                        <p className="text-xs text-gray-500">Total Users</p>
                        <p className="text-2xl font-bold text-blue-700">{activityOverview.total_users}</p>
                      </div>
                      <div className="bg-white/70 p-3 rounded-lg border border-green-200">
                        <p className="text-xs text-gray-500">Active Users (7d)</p>
                        <p className="text-2xl font-bold text-green-700">{activityOverview.active_users_7d}</p>
                      </div>
                      <div className="bg-white/70 p-3 rounded-lg border border-orange-200">
                        <p className="text-xs text-gray-500">Activity Types</p>
                        <p className="text-2xl font-bold text-orange-700">{Object.keys(activityOverview.activity_counts || {}).length}</p>
                      </div>
                    </div>
                    {/* Activity Type Breakdown */}
                    <div className="mt-4 flex flex-wrap gap-2">
                      {Object.entries(activityOverview.activity_counts || {}).map(([type, count]) => (
                        <Badge key={type} variant="outline" className="text-gray-700 border-gray-300">
                          {type.replace(/_/g, ' ')}: {count}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-700 text-sm font-medium">Today's Logins</p>
                        <p className="text-3xl font-bold text-green-900 mt-1">{dashboard.today.successful_logins}</p>
                      </div>
                      <div className="bg-green-200 p-3 rounded-full">
                        <CheckCircle className="h-6 w-6 text-green-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-red-700 text-sm font-medium">Failed Logins Today</p>
                        <p className="text-3xl font-bold text-red-900 mt-1">{dashboard.today.failed_logins}</p>
                      </div>
                      <div className="bg-red-200 p-3 rounded-full">
                        <XCircle className="h-6 w-6 text-red-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-orange-700 text-sm font-medium">Active Alerts</p>
                        <p className="text-3xl font-bold text-orange-900 mt-1">
                          {dashboard.alerts.critical + dashboard.alerts.warning}
                        </p>
                        {dashboard.alerts.critical > 0 && (
                          <p className="text-xs text-red-600 font-medium mt-1">{dashboard.alerts.critical} critical</p>
                        )}
                      </div>
                      <div className="bg-orange-200 p-3 rounded-full">
                        <AlertTriangle className="h-6 w-6 text-orange-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-700 text-sm font-medium">Blocked Users</p>
                        <p className="text-3xl font-bold text-purple-900 mt-1">{dashboard.users.blocked}</p>
                        {dashboard.users.flagged > 0 && (
                          <p className="text-xs text-yellow-600 font-medium mt-1">{dashboard.users.flagged} flagged</p>
                        )}
                      </div>
                      <div className="bg-purple-200 p-3 rounded-full">
                        <Ban className="h-6 w-6 text-purple-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Alerts - White Background */}
              {dashboard.alerts.recent && dashboard.alerts.recent.length > 0 && (
                <Card className="bg-white border-gray-200 shadow-sm">
                  <CardHeader className="border-b border-gray-100">
                    <CardTitle className="text-gray-900 flex items-center gap-2">
                      <AlertOctagon className="h-5 w-5 text-red-500" />
                      Recent Security Alerts
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      {dashboard.alerts.recent.slice(0, 5).map((alert) => (
                        <div 
                          key={alert.id} 
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <Badge className={getAlertLevelBadge(alert.alert_level)}>
                              {alert.alert_level}
                            </Badge>
                            <div>
                              <p className="text-gray-900 font-medium">{alert.title}</p>
                              <p className="text-gray-600 text-sm">
                                {alert.user?.email || "Unknown user"} • {formatDate(alert.created_at)}
                              </p>
                            </div>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setSelectedUser(alert.user);
                              fetchUserSummary(alert.user_id);
                            }}
                            className="text-purple-600 border-purple-300 hover:bg-purple-50"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* Live Feed Tab */}
        <TabsContent value="live-feed" className="mt-6">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100">
              <div>
                <CardTitle className="text-gray-900 flex items-center gap-2">
                  <Zap className="h-5 w-5 text-yellow-500" />
                  Live Activity Feed
                  {wsConnected && <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse ml-2"></span>}
                </CardTitle>
                <CardDescription className="text-gray-600">
                  Real-time user activities as they happen
                </CardDescription>
              </div>
              <div className="flex gap-2">
                {!wsConnected && (
                  <Button 
                    onClick={connectWebSocket}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Start Live Feed
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {liveActivities.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No activities yet. Click "Backfill History" to import past activities or start the live feed.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {liveActivities.map((activity, idx) => (
                    <div 
                      key={activity.id || idx} 
                      className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                        idx === 0 && wsConnected ? 'bg-yellow-50 border-yellow-300 animate-pulse' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          activity.activity_type?.includes('completed') ? 'bg-green-100 text-green-600' :
                          activity.activity_type?.includes('started') ? 'bg-blue-100 text-blue-600' :
                          activity.activity_type === 'login' ? 'bg-purple-100 text-purple-600' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {getActivityIcon(activity.activity_type)}
                        </div>
                        <div>
                          <p className="text-gray-900 font-medium capitalize">
                            {activity.activity_type?.replace(/_/g, ' ')}
                          </p>
                          <p className="text-gray-600 text-sm">{activity.description}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs text-gray-500">
                              {activity.user_email || activity.user_name || 'Unknown User'}
                            </Badge>
                            {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                              <Badge variant="outline" className="text-xs text-blue-500">
                                +{Object.keys(activity.metadata).length} details
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-500 text-sm">{formatDate(activity.created_at)}</p>
                        {idx === 0 && wsConnected && (
                          <Badge className="bg-green-100 text-green-700 text-xs mt-1">New</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="mt-6">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100">
              <div>
                <CardTitle className="text-gray-900">Security Alerts</CardTitle>
                <CardDescription className="text-gray-600">
                  All suspicious activities detected by the system
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => fetchAlerts("active")}
                  className="text-sm border-gray-300"
                >
                  Active Only
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => fetchAlerts()}
                  className="text-sm border-gray-300"
                >
                  All
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {alerts.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Shield className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No security alerts at this time</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50 border-gray-200">
                      <TableHead className="text-gray-700 font-semibold">Level</TableHead>
                      <TableHead className="text-gray-700 font-semibold">Alert</TableHead>
                      <TableHead className="text-gray-700 font-semibold">User</TableHead>
                      <TableHead className="text-gray-700 font-semibold">Time</TableHead>
                      <TableHead className="text-gray-700 font-semibold">Status</TableHead>
                      <TableHead className="text-gray-700 font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {alerts.map((alert) => (
                      <TableRow key={alert.id} className="border-gray-200 hover:bg-gray-50">
                        <TableCell>
                          <Badge className={getAlertLevelBadge(alert.alert_level)}>
                            {alert.alert_level}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-gray-900 font-medium">{alert.title}</p>
                            <p className="text-gray-500 text-sm">{alert.description}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-700">
                          {alert.user?.email || "Unknown"}
                        </TableCell>
                        <TableCell className="text-gray-600 text-sm">
                          {formatDate(alert.created_at)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={alert.status === "active" ? "destructive" : "secondary"}>
                            {alert.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setSelectedUser(alert.user);
                                fetchUserSummary(alert.user_id);
                              }}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {alert.status === "active" && (
                              <>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleDismissAlert(alert.id)}
                                  className="text-yellow-600 hover:text-yellow-800"
                                >
                                  Dismiss
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleResolveAlert(alert.id)}
                                  className="text-green-600 hover:text-green-800"
                                >
                                  Resolve
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* User Lookup Tab */}
        <TabsContent value="users" className="mt-6">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="border-b border-gray-100">
              <CardTitle className="text-gray-900">User Activity Lookup</CardTitle>
              <CardDescription className="text-gray-600">
                Search for a user to view their complete activity history
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="flex gap-4 mb-6">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by email or name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && searchUsers()}
                    className="pl-10 bg-white border-gray-300"
                    data-testid="user-search-input"
                  />
                </div>
                <Button 
                  onClick={searchUsers}
                  disabled={searching}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="user-search-btn"
                >
                  {searching ? "Searching..." : "Search"}
                </Button>
              </div>

              {searchResults.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600 font-medium">Search Results ({searchResults.length})</p>
                  <div className="space-y-2">
                    {searchResults.map((user) => (
                      <div 
                        key={user.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 cursor-pointer transition-colors"
                        onClick={() => {
                          setSelectedUser(user);
                          fetchUserSummary(user.id);
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-medium">
                            {user.name?.charAt(0) || "U"}
                          </div>
                          <div>
                            <p className="text-gray-900 font-medium">{user.name}</p>
                            <p className="text-gray-600 text-sm">{user.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {user.is_blocked && (
                            <Badge className="bg-red-100 text-red-700">Blocked</Badge>
                          )}
                          {user.is_flagged && (
                            <Badge className="bg-yellow-100 text-yellow-700">Flagged</Badge>
                          )}
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* User Detail Modal - With Complete Activity History */}
      <Dialog open={!!userSummary} onOpenChange={(open) => !open && setUserSummary(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-white border-gray-200">
          {userSummary && (
            <>
              <DialogHeader className="border-b border-gray-200 pb-4">
                <DialogTitle className="text-xl text-gray-900 flex items-center gap-2">
                  <Users className="h-5 w-5 text-purple-600" />
                  Complete User Activity History
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  Detailed activity and security profile for {userSummary.user.email}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* User Info Header */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                      {userSummary.user.name?.charAt(0) || "U"}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{userSummary.user.name}</h3>
                      <p className="text-gray-600">{userSummary.user.email}</p>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="outline" className="text-purple-600 border-purple-300">
                          {userSummary.user.role}
                        </Badge>
                        {userSummary.user.is_verified && (
                          <Badge className="bg-green-100 text-green-700">Verified</Badge>
                        )}
                        {userSummary.security?.is_blocked && (
                          <Badge className="bg-red-100 text-red-700">Blocked</Badge>
                        )}
                        {userSummary.security?.is_flagged && (
                          <Badge className="bg-yellow-100 text-yellow-700">Flagged</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className={`px-4 py-2 rounded-lg border ${getRiskBadge(userSummary.security?.risk_score || 0)}`}>
                      <p className="text-xs opacity-70">Risk Score</p>
                      <p className="text-2xl font-bold">{userSummary.security?.risk_score || 0}</p>
                    </div>
                    <div className="flex gap-2">
                      {userSummary.security?.is_blocked ? (
                        <Button size="sm" onClick={() => handleUnblockUser(userSummary.user.id)} className="bg-green-600 hover:bg-green-700 text-white">
                          <Unlock className="h-4 w-4 mr-1" /> Unblock
                        </Button>
                      ) : (
                        <Button size="sm" onClick={() => handleBlockUser(userSummary.user.id)} variant="destructive">
                          <Lock className="h-4 w-4 mr-1" /> Block
                        </Button>
                      )}
                      {userSummary.security?.is_flagged ? (
                        <Button size="sm" onClick={() => handleUnflagUser(userSummary.user.id)} variant="outline" className="border-gray-300">
                          Remove Flag
                        </Button>
                      ) : (
                        <Button size="sm" onClick={() => handleFlagUser(userSummary.user.id)} variant="outline" className="text-yellow-600 border-yellow-300 hover:bg-yellow-50">
                          <Flag className="h-4 w-4 mr-1" /> Flag
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Security Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-4">
                      <p className="text-blue-700 text-sm font-medium">Total Logins</p>
                      <p className="text-2xl font-bold text-blue-900">{userSummary.security?.total_logins || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="p-4">
                      <p className="text-red-700 text-sm font-medium">Failed Logins (30d)</p>
                      <p className="text-2xl font-bold text-red-900">{userSummary.security?.failed_logins_30d || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-50 border-purple-200">
                    <CardContent className="p-4">
                      <p className="text-purple-700 text-sm font-medium">Devices Used</p>
                      <p className="text-2xl font-bold text-purple-900">{userSummary.security?.devices_used || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-orange-50 border-orange-200">
                    <CardContent className="p-4">
                      <p className="text-orange-700 text-sm font-medium">Active Alerts</p>
                      <p className="text-2xl font-bold text-orange-900">{userSummary.security?.active_alerts || 0}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Activity Tabs with Full Details */}
                <Tabs defaultValue="assessments" className="w-full">
                  <TabsList className="bg-gray-100 border border-gray-300">
                    <TabsTrigger value="assessments" className="text-gray-700 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                      <FileText className="h-4 w-4 mr-1" />
                      Assessments ({userSummary.assessments?.total || 0})
                    </TabsTrigger>
                    <TabsTrigger value="interviews" className="text-gray-700 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                      <Mic className="h-4 w-4 mr-1" />
                      Interviews ({userSummary.interviews?.total || 0})
                    </TabsTrigger>
                    <TabsTrigger value="skill-dna" className="text-gray-700 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                      <Brain className="h-4 w-4 mr-1" />
                      Skill DNA
                    </TabsTrigger>
                    <TabsTrigger value="career-twin" className="text-gray-700 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                      <Target className="h-4 w-4 mr-1" />
                      Career Twin
                    </TabsTrigger>
                    <TabsTrigger value="activities" className="text-gray-700 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                      <Activity className="h-4 w-4 mr-1" />
                      All Activity
                    </TabsTrigger>
                  </TabsList>

                  {/* Assessments Tab - Detailed History */}
                  <TabsContent value="assessments" className="mt-4">
                    {userSummary.assessments?.recent?.length > 0 ? (
                      <Accordion type="single" collapsible className="space-y-2">
                        {userSummary.assessments.recent.map((assessment, idx) => (
                          <AccordionItem key={idx} value={`assessment-${idx}`} className="border border-gray-200 rounded-lg bg-white">
                            <AccordionTrigger className="px-4 py-3 hover:bg-gray-50">
                              <div className="flex items-center justify-between w-full pr-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                    <ClipboardList className="h-5 w-5 text-purple-600" />
                                  </div>
                                  <div className="text-left">
                                    <p className="text-gray-900 font-medium">
                                      {assessment.career_field || assessment.assessment_name || "Career Assessment"}
                                    </p>
                                    <p className="text-gray-500 text-sm">{formatDate(assessment.created_at)}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {assessment.overall_score && (
                                    <Badge className="bg-green-100 text-green-700">
                                      Score: {assessment.overall_score}%
                                    </Badge>
                                  )}
                                  <Badge className="bg-blue-100 text-blue-700">
                                    {assessment.status || "completed"}
                                  </Badge>
                                </div>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4">
                              <div className="space-y-4 pt-2 border-t border-gray-100">
                                {/* Score Breakdown */}
                                {assessment.scores && (
                                  <div className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                      <Star className="h-4 w-4 text-yellow-500" />
                                      Score Breakdown
                                    </h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                      {Object.entries(assessment.scores || {}).map(([key, value]) => (
                                        <div key={key} className="bg-white p-3 rounded border border-gray-200">
                                          <p className="text-xs text-gray-500 capitalize">{key.replace(/_/g, ' ')}</p>
                                          <p className="text-lg font-bold text-gray-900">{value}%</p>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Questions & Answers */}
                                {assessment.questions && assessment.questions.length > 0 && (
                                  <div className="bg-blue-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                      <MessageSquare className="h-4 w-4 text-blue-500" />
                                      Questions & Answers ({assessment.questions.length})
                                    </h4>
                                    <div className="space-y-3 max-h-60 overflow-y-auto">
                                      {assessment.questions.map((q, qIdx) => (
                                        <div key={qIdx} className="bg-white p-3 rounded border border-blue-200">
                                          <p className="text-sm text-gray-700 font-medium">Q{qIdx + 1}: {q.question || q.text}</p>
                                          <p className="text-sm text-blue-700 mt-1">
                                            Answer: {q.selected_answer || q.user_answer || q.answer || "N/A"}
                                          </p>
                                          {q.custom_answer && (
                                            <p className="text-sm text-purple-700 mt-1 italic">
                                              Custom Input: {q.custom_answer}
                                            </p>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Recommendations */}
                                {assessment.recommendations && assessment.recommendations.length > 0 && (
                                  <div className="bg-green-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                      <Award className="h-4 w-4 text-green-500" />
                                      AI Recommendations
                                    </h4>
                                    <ul className="space-y-2">
                                      {assessment.recommendations.map((rec, rIdx) => (
                                        <li key={rIdx} className="flex items-start gap-2 text-sm text-gray-700">
                                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                          {typeof rec === 'string' ? rec : rec.text || rec.recommendation}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {/* Career Matches */}
                                {assessment.career_matches && assessment.career_matches.length > 0 && (
                                  <div className="bg-purple-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                      <Target className="h-4 w-4 text-purple-500" />
                                      Career Matches
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                      {assessment.career_matches.map((career, cIdx) => (
                                        <Badge key={cIdx} className="bg-purple-100 text-purple-700">
                                          {typeof career === 'string' ? career : career.title || career.name}
                                          {career.match_score && ` (${career.match_score}%)`}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Strengths & Weaknesses */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {assessment.strengths && assessment.strengths.length > 0 && (
                                    <div className="bg-emerald-50 p-4 rounded-lg">
                                      <h4 className="font-semibold text-gray-900 mb-2 text-sm">Strengths</h4>
                                      <div className="flex flex-wrap gap-1">
                                        {assessment.strengths.map((s, sIdx) => (
                                          <Badge key={sIdx} className="bg-emerald-100 text-emerald-700 text-xs">
                                            {typeof s === 'string' ? s : s.name}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {assessment.weaknesses && assessment.weaknesses.length > 0 && (
                                    <div className="bg-amber-50 p-4 rounded-lg">
                                      <h4 className="font-semibold text-gray-900 mb-2 text-sm">Areas to Improve</h4>
                                      <div className="flex flex-wrap gap-1">
                                        {assessment.weaknesses.map((w, wIdx) => (
                                          <Badge key={wIdx} className="bg-amber-100 text-amber-700 text-xs">
                                            {typeof w === 'string' ? w : w.name}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    ) : (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-gray-500">No assessments found for this user</p>
                      </div>
                    )}
                  </TabsContent>

                  {/* Interviews Tab - Detailed History */}
                  <TabsContent value="interviews" className="mt-4">
                    {userSummary.interviews?.recent?.length > 0 ? (
                      <Accordion type="single" collapsible className="space-y-2">
                        {userSummary.interviews.recent.map((interview, idx) => (
                          <AccordionItem key={idx} value={`interview-${idx}`} className="border border-gray-200 rounded-lg bg-white">
                            <AccordionTrigger className="px-4 py-3 hover:bg-gray-50">
                              <div className="flex items-center justify-between w-full pr-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                    <Video className="h-5 w-5 text-blue-600" />
                                  </div>
                                  <div className="text-left">
                                    <p className="text-gray-900 font-medium">
                                      {interview.career_field || interview.role || "Interview Session"}
                                    </p>
                                    <p className="text-gray-500 text-sm">{formatDate(interview.created_at)}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {interview.overall_score && (
                                    <Badge className="bg-green-100 text-green-700">
                                      Score: {interview.overall_score}%
                                    </Badge>
                                  )}
                                  <Badge className={interview.completed ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>
                                    {interview.completed ? "Completed" : "In Progress"}
                                  </Badge>
                                </div>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4">
                              <div className="space-y-4 pt-2 border-t border-gray-100">
                                {/* Interview Config */}
                                <div className="bg-gray-50 p-4 rounded-lg">
                                  <h4 className="font-semibold text-gray-900 mb-3">Interview Details</h4>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <div className="bg-white p-3 rounded border border-gray-200">
                                      <p className="text-xs text-gray-500">Career Field</p>
                                      <p className="text-sm font-medium text-gray-900">{interview.career_field || "N/A"}</p>
                                    </div>
                                    <div className="bg-white p-3 rounded border border-gray-200">
                                      <p className="text-xs text-gray-500">Experience Level</p>
                                      <p className="text-sm font-medium text-gray-900">{interview.experience_level || "N/A"}</p>
                                    </div>
                                    <div className="bg-white p-3 rounded border border-gray-200">
                                      <p className="text-xs text-gray-500">Questions Asked</p>
                                      <p className="text-sm font-medium text-gray-900">{interview.questions?.length || interview.total_questions || 0}</p>
                                    </div>
                                    <div className="bg-white p-3 rounded border border-gray-200">
                                      <p className="text-xs text-gray-500">Duration</p>
                                      <p className="text-sm font-medium text-gray-900">{interview.duration_minutes || "N/A"} mins</p>
                                    </div>
                                  </div>
                                </div>

                                {/* Q&A History */}
                                {interview.questions && interview.questions.length > 0 && (
                                  <div className="bg-blue-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                      <MessageSquare className="h-4 w-4 text-blue-500" />
                                      Interview Q&A
                                    </h4>
                                    <div className="space-y-3 max-h-80 overflow-y-auto">
                                      {interview.questions.map((qa, qaIdx) => (
                                        <div key={qaIdx} className="bg-white p-4 rounded border border-blue-200">
                                          <p className="text-sm text-gray-800 font-medium mb-2">
                                            Q{qaIdx + 1}: {qa.question || qa.text}
                                          </p>
                                          <p className="text-sm text-blue-700 bg-blue-50 p-2 rounded">
                                            <strong>Answer:</strong> {qa.answer || qa.user_answer || "No response recorded"}
                                          </p>
                                          {qa.feedback && (
                                            <p className="text-sm text-green-700 mt-2 bg-green-50 p-2 rounded">
                                              <strong>AI Feedback:</strong> {qa.feedback}
                                            </p>
                                          )}
                                          {qa.score && (
                                            <div className="mt-2 flex items-center gap-2">
                                              <Star className="h-4 w-4 text-yellow-500" />
                                              <span className="text-sm font-medium text-gray-700">Score: {qa.score}/10</span>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Overall Feedback */}
                                {interview.overall_feedback && (
                                  <div className="bg-green-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                      <Award className="h-4 w-4 text-green-500" />
                                      Overall Feedback
                                    </h4>
                                    <p className="text-gray-700 text-sm">{interview.overall_feedback}</p>
                                  </div>
                                )}

                                {/* ML Analysis if available */}
                                {interview.ml_analysis && (
                                  <div className="bg-purple-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-gray-900 mb-3">ML Analysis Results</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                      {interview.ml_analysis.confidence && (
                                        <div className="bg-white p-3 rounded border border-purple-200">
                                          <p className="text-xs text-gray-500">Confidence</p>
                                          <p className="text-lg font-bold text-purple-700">{interview.ml_analysis.confidence}%</p>
                                        </div>
                                      )}
                                      {interview.ml_analysis.engagement && (
                                        <div className="bg-white p-3 rounded border border-purple-200">
                                          <p className="text-xs text-gray-500">Engagement</p>
                                          <p className="text-lg font-bold text-purple-700">{interview.ml_analysis.engagement}%</p>
                                        </div>
                                      )}
                                      {interview.ml_analysis.communication && (
                                        <div className="bg-white p-3 rounded border border-purple-200">
                                          <p className="text-xs text-gray-500">Communication</p>
                                          <p className="text-lg font-bold text-purple-700">{interview.ml_analysis.communication}%</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    ) : (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <Mic className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-gray-500">No interviews found for this user</p>
                      </div>
                    )}
                  </TabsContent>

                  {/* Skill DNA Tab */}
                  <TabsContent value="skill-dna" className="mt-4">
                    {userSummary.skill_dna ? (
                      <div className="space-y-4">
                        <Card className="bg-white border-gray-200">
                          <CardHeader>
                            <CardTitle className="text-gray-900 flex items-center gap-2">
                              <Brain className="h-5 w-5 text-purple-500" />
                              Skill DNA Profile
                            </CardTitle>
                            <CardDescription className="text-gray-500">
                              Last updated: {formatDate(userSummary.skill_dna.updated_at || userSummary.skill_dna.created_at)}
                            </CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            {/* Skills */}
                            {userSummary.skill_dna.skills && userSummary.skill_dna.skills.length > 0 && (
                              <div className="bg-blue-50 p-4 rounded-lg">
                                <h4 className="font-semibold text-gray-900 mb-3">Skills ({userSummary.skill_dna.skills.length})</h4>
                                <div className="flex flex-wrap gap-2">
                                  {userSummary.skill_dna.skills.map((skill, idx) => (
                                    <Badge key={idx} className="bg-blue-100 text-blue-700">
                                      {typeof skill === 'string' ? skill : skill.name}
                                      {skill.level && ` (${skill.level})`}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Proficiency Scores */}
                            {userSummary.skill_dna.proficiency_scores && (
                              <div className="bg-green-50 p-4 rounded-lg">
                                <h4 className="font-semibold text-gray-900 mb-3">Proficiency Scores</h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                  {Object.entries(userSummary.skill_dna.proficiency_scores).map(([key, value]) => (
                                    <div key={key} className="bg-white p-3 rounded border border-green-200">
                                      <p className="text-xs text-gray-500 capitalize">{key.replace(/_/g, ' ')}</p>
                                      <p className="text-lg font-bold text-green-700">{value}%</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <Brain className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-gray-500">No Skill DNA profile found for this user</p>
                      </div>
                    )}
                  </TabsContent>

                  {/* Career Twin Tab */}
                  <TabsContent value="career-twin" className="mt-4">
                    {userSummary.career_twin ? (
                      <Card className="bg-white border-gray-200">
                        <CardHeader>
                          <CardTitle className="text-gray-900 flex items-center gap-2">
                            <Target className="h-5 w-5 text-pink-500" />
                            Career Twin Profile
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {/* Career Goals */}
                          {userSummary.career_twin.career_goals && (
                            <div className="bg-pink-50 p-4 rounded-lg">
                              <h4 className="font-semibold text-gray-900 mb-2">Career Goals</h4>
                              <p className="text-gray-700">{userSummary.career_twin.career_goals}</p>
                            </div>
                          )}
                          
                          {/* Current Role */}
                          {userSummary.career_twin.current_role && (
                            <div className="bg-purple-50 p-4 rounded-lg">
                              <h4 className="font-semibold text-gray-900 mb-2">Current Role</h4>
                              <p className="text-gray-700">{userSummary.career_twin.current_role}</p>
                            </div>
                          )}
                          
                          {/* Target Roles */}
                          {userSummary.career_twin.target_roles && userSummary.career_twin.target_roles.length > 0 && (
                            <div className="bg-blue-50 p-4 rounded-lg">
                              <h4 className="font-semibold text-gray-900 mb-2">Target Roles</h4>
                              <div className="flex flex-wrap gap-2">
                                {userSummary.career_twin.target_roles.map((role, idx) => (
                                  <Badge key={idx} className="bg-blue-100 text-blue-700">
                                    {typeof role === 'string' ? role : role.title}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Avatar Config */}
                          {userSummary.career_twin.avatar_config && (
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <h4 className="font-semibold text-gray-900 mb-2">Avatar Configuration</h4>
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(userSummary.career_twin.avatar_config).map(([key, value]) => (
                                  <Badge key={key} variant="outline" className="text-gray-700">
                                    {key}: {value}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <Target className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-gray-500">No Career Twin profile found for this user</p>
                      </div>
                    )}
                  </TabsContent>

                  {/* All Activities Tab */}
                  <TabsContent value="activities" className="mt-4">
                    {userSummary.recent_activities?.length > 0 ? (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {userSummary.recent_activities.map((activity, idx) => (
                          <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-600">
                                {getActivityIcon(activity.activity_type)}
                              </div>
                              <div>
                                <p className="text-gray-900 font-medium capitalize">{activity.activity_type?.replace(/_/g, ' ')}</p>
                                <p className="text-gray-600 text-sm">{activity.description}</p>
                                {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {Object.entries(activity.metadata).slice(0, 3).map(([key, value]) => {
                                      // Handle different value types safely
                                      let displayValue = '';
                                      if (value === null || value === undefined) {
                                        displayValue = 'N/A';
                                      } else if (typeof value === 'string') {
                                        displayValue = value.length > 20 ? value.substring(0, 20) + '...' : value;
                                      } else if (typeof value === 'number' || typeof value === 'boolean') {
                                        displayValue = String(value);
                                      } else if (Array.isArray(value)) {
                                        displayValue = `[${value.length} items]`;
                                      } else if (typeof value === 'object') {
                                        displayValue = `{${Object.keys(value).join(', ')}}`;
                                      } else {
                                        displayValue = String(value);
                                      }
                                      return (
                                        <Badge key={key} variant="outline" className="text-xs text-gray-500">
                                          {key}: {displayValue}
                                        </Badge>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                            <p className="text-gray-500 text-sm whitespace-nowrap">{formatDate(activity.created_at)}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <Activity className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-gray-500">No activities recorded for this user</p>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>

                {/* Login Locations */}
                {userSummary.security?.locations?.length > 0 && (
                  <Card className="bg-white border-gray-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-gray-700 flex items-center gap-2">
                        <Globe className="h-4 w-4 text-blue-500" />
                        Login Locations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {userSummary.security.locations.map((location, idx) => (
                          <Badge key={idx} variant="outline" className="text-gray-700 border-gray-300">
                            {location}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserActivityMonitor;
